import { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import LandingPageV2 from './LandingPageV2'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import axios from 'axios'
// The material-icons package CSS is no longer imported: it declared all five
// icon variants (filled, outlined, round, sharp, two-tone) and dragged ~1.5 MB
// of fonts into the build. index.css now declares a subset of the filled font.
import ProtectedRoute from './core/Container/ProtectedRoute'
import PaginaErro from './PaginaErro'
import { ROUTES } from './appRoutes'
import ErrorBoundary from './core/DS/ErrorBoundary/ErrorBoundary'
import Loading from './core/DS/Loading/Loading'
import { CurrentUserProvider } from './core/Utils/CurrentUserContext'

// Only the public landing page is bundled eagerly. Everything else — the login
// screen, the whole logged-in system and the legacy landing — is split into its
// own chunk, so a visitor arriving at "/" no longer downloads the admin app.
const LandingPage = lazy(() => import('./LandingPage'))
const Login = lazy(() => import('./core/Pages/Login/Login'))
const DashboardContainer = lazy(() => import('./core/Pages/Dashboard/DashboardContainer'))
const PatientsContainer = lazy(() => import('./core/Pages/Patients/PatientsContainer'))
const SessionsContainer = lazy(() => import('./core/Pages/Sessions/SessionsContainer'))
const AccountabilityContainer = lazy(() => import('./core/Pages/Accountability/AccountabilityContainer'))
const PlansContainer = lazy(() => import('./core/Pages/Plans/PlansContainer'))
const TherapistsContainer = lazy(() => import('./core/Pages/Therapists/TherapistsContainer'))
const GamesContainer = lazy(() => import('./core/Pages/Games/GamesContainer'))
const ThemesContainer = lazy(() => import('./core/Pages/Themes/ThemesContainer'))
const AcervoContainer = lazy(() => import('./core/Pages/Acervo/AcervoContainer'))

axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL
axios.defaults.headers.post['Content-Type'] = 'application/json'

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname
      if (currentPath !== ROUTES.login) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        window.location.href = ROUTES.login
      }
    }
    return Promise.reject(error)
  }
)

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <ErrorBoundary>
    <BrowserRouter>
      <CurrentUserProvider>
        <Suspense fallback={<Loading />}>
          {/*
            Só "/" mora na raiz. Todo o sistema interno fica embaixo de ROUTES,
            que carrega o prefixo /app - o mesmo que o .htaccess do servidor
            libera com uma regra única. Rota interna nova não precisa de
            nenhuma edição no servidor. Ver src/appRoutes.ts.
          */}
          <Routes>
            <Route path="/" element={<LandingPageV2 />} />
            <Route path={ROUTES.landingAntiga} element={<LandingPage />} />
            <Route path={ROUTES.login} element={<Login />} />
            <Route path={ROUTES.dashboard} element={<ProtectedRoute><DashboardContainer /></ProtectedRoute>} />
            <Route path={ROUTES.patients} element={<ProtectedRoute><PatientsContainer /></ProtectedRoute>} />
            <Route path={ROUTES.sessions} element={<ProtectedRoute><SessionsContainer /></ProtectedRoute>} />
            <Route path={ROUTES.plans} element={<ProtectedRoute adminOnly><PlansContainer /></ProtectedRoute>} />
            <Route path={ROUTES.therapists} element={<ProtectedRoute adminOnly><TherapistsContainer /></ProtectedRoute>} />
            <Route path={ROUTES.accountability} element={<ProtectedRoute><AccountabilityContainer /></ProtectedRoute>} />
            <Route path={ROUTES.games} element={<ProtectedRoute><GamesContainer /></ProtectedRoute>} />
            <Route path={`${ROUTES.games}/:jogoId`} element={<ProtectedRoute><GamesContainer /></ProtectedRoute>} />
            <Route path={ROUTES.themes} element={<ProtectedRoute adminOnly><ThemesContainer /></ProtectedRoute>} />
            <Route path={ROUTES.acervo} element={<ProtectedRoute adminOnly><AcervoContainer /></ProtectedRoute>} />
            <Route path="*" element={<PaginaErro />} />
          </Routes>
        </Suspense>
      </CurrentUserProvider>
    </BrowserRouter>
  </ErrorBoundary>
);
